import { useQuery } from "@tanstack/react-query";
import {
  getGridSnapshot,
  getGridDetail,
  getGridHistory,
  getGenerationMix,
  type GridSnapshotResponse,
  type GridRegionImpact,
  type GridHistoryResponse,
  type GenerationMixResponse,
} from "../../../api/grid";

/** All BA impacts for a single hour. Only fetches when enabled. */
export function useGridSnapshot(hour?: string, enabled: boolean = true) {
  return useQuery<GridSnapshotResponse>({
    queryKey: ["grid", "snapshot", hour ?? "latest"],
    queryFn: () => getGridSnapshot(hour),
    enabled,
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
  });
}

/** Full ReCiPe detail for one BA at a given hour. */
export function useGridDetail(baCode: string | null, hour?: string) {
  return useQuery<GridRegionImpact>({
    queryKey: ["grid", "detail", baCode, hour ?? "latest"],
    queryFn: () => getGridDetail(baCode!, hour),
    enabled: !!baCode,
    staleTime: 5 * 60_000,
    gcTime: 5 * 60_000,        // Evict when BA deselected + 5 min
  });
}

/** 24h history for a BA (sparkline / charts). */
export function useGridHistory(baCode: string | null, start?: string, end?: string) {
  return useQuery<GridHistoryResponse>({
    queryKey: ["grid", "history", baCode, start, end],
    queryFn: () => getGridHistory(baCode!, start, end),
    enabled: !!baCode,
    staleTime: 5 * 60_000,
    gcTime: 5 * 60_000,        // Evict when BA deselected + 5 min
  });
}

/** Fuel-type generation mix for one BA. Pass period="annual" for baseline view. */
export function useGenerationMix(baCode: string | null, hour?: string, period?: string) {
  return useQuery<GenerationMixResponse>({
    queryKey: ["grid", "generation-mix", baCode, hour ?? "latest", period ?? "hour"],
    queryFn: () => getGenerationMix(baCode!, hour, period),
    enabled: !!baCode,
    staleTime: period === "annual" ? 30 * 60_000 : 5 * 60_000,
    gcTime: period === "annual" ? 30 * 60_000 : 5 * 60_000,
  });
}
